import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import {
  AFFIRMATIONS,
  INVALID_TOKEN_MESSAGE,
  SUBMITTED_MESSAGE,
} from '@/features/memberApplications/applicationContent';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/member-application-public`;

export default function PublicApplication() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [associationName, setAssociationName] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    city_state: '',
    email: '',
    phone: '',
    date_of_birth: '',
    typed_signature: '',
  });
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'load', token }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json?.valid) {
          setValid(true);
          setAssociationName(json.association_name || '');
          setPersonalNote(json.personal_note || '');
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  async function submit() {
    setError('');
    if (!form.full_name.trim() || !form.email.trim() || !form.typed_signature.trim()) {
      setError('Please fill in your name, your email, and your signature.');
      return;
    }
    if (!AFFIRMATIONS.every((a) => agreed[a.key])) {
      setError('Please check both boxes to send this in.');
      return;
    }
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'submit',
          token,
          ...form,
          affirmations: AFFIRMATIONS.map((a) => ({ key: a.key, text: a.text, agreed_at: now })),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.error || 'We could not send that. Please try again.');
        return;
      }
      setDone(true);
    } catch {
      setError('We could not send that. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <p className="max-w-md text-center text-muted-foreground">{INVALID_TOKEN_MESSAGE}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <p className="max-w-md text-center">{SUBMITTED_MESSAGE}</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background p-4">
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div>
          <h1 className="text-2xl font-semibold">{associationName}</h1>
          <p className="text-sm text-muted-foreground mt-1">Application for membership</p>
        </div>

        {personalNote && (
          <Card>
            <CardContent className="pt-6 text-sm whitespace-pre-wrap">{personalNote}</CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">About you</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city_state">City and state</Label>
              <Input
                id="city_state"
                value={form.city_state}
                onChange={(e) => setForm({ ...form, city_state: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of birth</Label>
              <Input
                id="dob"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">What you are agreeing to</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {AFFIRMATIONS.map((a) => (
              <label key={a.key} className="flex gap-3 items-start cursor-pointer">
                <Checkbox
                  checked={!!agreed[a.key]}
                  onCheckedChange={(checked) => setAgreed({ ...agreed, [a.key]: !!checked })}
                  className="mt-1"
                />
                <span className="text-sm">{a.text}</span>
              </label>
            ))}
            <div className="space-y-2">
              <Label htmlFor="typed_signature">Type your name as your signature</Label>
              <Input
                id="typed_signature"
                value={form.typed_signature}
                onChange={(e) => setForm({ ...form, typed_signature: e.target.value })}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button onClick={submit} disabled={submitting} className="min-h-11 w-full sm:w-auto">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                'Send this in'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
